import Checkbox from '@mui/material/Checkbox';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from 'src/typings/translation';
import { ActionButton } from '../Button/Button';

export type ColumnOption<T> = {
  title: TranslationKey;
  field: keyof T;
  disabled?: boolean;
};

type ColumnMenuProps<T> = {
  columns: ColumnOption<T>[];
  selectedColumns: ColumnOption<T>[];
  onColumnsChange: (columns: ColumnOption<T>[]) => void;
};

export function ColumnMenu<T>({
  columns = [],
  selectedColumns = [],
  onColumnsChange
}: ColumnMenuProps<T>) {
  const { t } = useTranslation();

  const [selected, setSelected] = useState(selectedColumns);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const onOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const onSelect = (option: ColumnOption<T>) => {
    const isSelected = selected.some(s => s.field === option.field);
    const columns = isSelected
      ? selected.filter(s => s.field !== option.field)
      : [...selected, option];

    setSelected(columns);
    onColumnsChange(columns);
  };

  return (
    <>
      <ActionButton
        id="menu-button"
        icon="la la-ellipsis-v"
        aria-controls={open ? 'column-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={onOpen}
      />

      <Menu
        id="column-menu"
        MenuListProps={{
          'aria-labelledby': 'menu-button'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            width: '16rem'
          }
        }}
      >
        {columns.map(column => (
          <MenuItem
            key={column.field as string}
            onClick={() => onSelect(column)}
            disabled={!!column.disabled}
          >
            <ListItemIcon>
              <Checkbox
                checked={selected.some(o => o.field === column.field)}
              />
            </ListItemIcon>
            <ListItemText>{t(column.title)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function useColumns<T>(initialColumns: ColumnOption<T>[]) {
  const [columns, setColumns] = useState<ColumnOption<T>[]>(initialColumns);

  const isColumnVisible = (columns: ColumnOption<T>[], field: keyof T) =>
    columns.some(c => c.field === field);

  return {
    columns: columns,
    onColumnsChange: setColumns,
    isColumnVisible: isColumnVisible
  };
}
